import { Box, Skeleton, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { BarChart } from "@mui/x-charts/BarChart";
import { useMemo } from "react";
import useOtActivityStats from "../hooks/useOtActivityStats";
import { colors } from "../theme";

// Reuse the exact read/write colors from OtTimeSeriesChart's scatter series, so this box and
// the trend chart below it read as the same visual language rather than two unrelated palettes.
const READ_COLOR = "#4cceac";
const WRITE_COLOR = "#6c8ef7";
const ERROR_COLOR = "#ff4444";
const OK_COLOR = "#4cceac";
// Fixed-order categorical palette for protocols/tags — assigned by rank (most-used first), not
// by identity, since the sets shown here are small and host-specific.
const PALETTE = ["#459095", "#7b5fe0", "#b89400", "#ff9966", "#ffb422", "#ff6b35", "#7b8bff", "#33c2c2"];

function fmtDuration(seconds) {
  if (seconds == null || !isFinite(seconds)) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const totalMinutes = seconds / 60;
  if (totalMinutes < 60) return `${Math.round(totalMinutes)}m`;
  const totalHours = totalMinutes / 60;
  if (totalHours < 24) return `${Math.floor(totalHours)}h ${Math.round(totalMinutes % 60)}m`;
  return `${(totalHours / 24).toFixed(1)}d`;
}

function fmtLatency(ms) {
  if (ms == null || !isFinite(ms)) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

const MiniStat = ({ label, value, color }) => (
  <Box sx={{ textAlign: "center", px: 2.5, py: 1.5, borderRadius: 1.5, backgroundColor: "#1c1c1c", border: `1px solid ${colors.grey[800]}`, minWidth: 130, flex: "1 1 auto" }}>
    <Typography sx={{ fontSize: 10.5, color: colors.grey[400], textTransform: "uppercase", letterSpacing: 0.5, mb: 0.5, whiteSpace: "nowrap" }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 22, fontWeight: 700, fontFamily: "monospace", color: color ?? colors.grey[100] }}>
      {value}
    </Typography>
  </Box>
);

const MiniPie = ({ title, data }) => {
  if (!data.length) return null;
  return (
    <Box sx={{ flex: "1 1 260px", minWidth: 240 }}>
      <Typography sx={{ fontSize: 11.5, color: colors.grey[500], textAlign: "center", mb: 1 }}>{title}</Typography>
      <PieChart
        series={[{ data, innerRadius: 45, outerRadius: 90, paddingAngle: 2, cornerRadius: 4, highlightScope: { fade: "global", highlight: "item" } }]}
        height={220}
        slotProps={{ legend: { hidden: true } }}
        tooltip={{ trigger: "item" }}
      />
    </Box>
  );
};

const HourlyBar = ({ hourly }) => {
  if (!hourly?.some((h) => h.count > 0)) return null;
  return (
    <Box sx={{ flex: "1 1 260px", minWidth: 240 }}>
      <Typography sx={{ fontSize: 11.5, color: colors.grey[500], textAlign: "center", mb: 1 }}>
        Activity by hour of day
      </Typography>
      <BarChart
        height={220}
        series={[{ data: hourly.map((h) => h.count), color: "#459095" }]}
        xAxis={[{ scaleType: "band", data: hourly.map((h) => `${h.hour}h`), tickLabelStyle: { fontSize: 10, fill: colors.grey[500] } }]}
        yAxis={[{ tickLabelStyle: { fontSize: 10, fill: colors.grey[500] } }]}
        margin={{ left: 34, right: 12, top: 10, bottom: 26 }}
        slotProps={{ legend: { hidden: true } }}
      />
    </Box>
  );
};

// Host-only: which tags/addresses this device communicates on the most — helps spot which
// signals dominate its traffic without having to select each one individually in the chart above.
const TopTagsBar = ({ topTags }) => {
  if (!topTags?.length) return null;
  const sorted = [...topTags].sort((a, b) => a.count - b.count); // ascending, so the biggest bar ends up on top
  return (
    <Box sx={{ flex: "1 1 100%", mt: 1 }}>
      <Typography sx={{ fontSize: 11.5, color: colors.grey[500], textAlign: "center", mb: 1 }}>
        Top tags on this host
      </Typography>
      <BarChart
        layout="horizontal"
        height={38 * sorted.length + 50}
        series={[{ data: sorted.map((t) => t.count), color: "#7b5fe0" }]}
        yAxis={[{ scaleType: "band", data: sorted.map((t) => t.label), tickLabelStyle: { fontSize: 11, fill: colors.grey[400] } }]}
        xAxis={[{ tickLabelStyle: { fontSize: 10, fill: colors.grey[500] } }]}
        margin={{ left: 160, right: 20, top: 10, bottom: 26 }}
        slotProps={{ legend: { hidden: true } }}
      />
    </Box>
  );
};

const ScopeBody = ({ data, loading, error, showHostExtras }) => {
  const rwData = useMemo(() => {
    if (!data) return [];
    return [
      { id: "read", value: data.readCount, label: "Read", color: READ_COLOR },
      { id: "write", value: data.writeCount, label: "Write", color: WRITE_COLOR },
    ].filter((d) => d.value > 0);
  }, [data]);

  const errorData = useMemo(() => {
    if (!data || !data.errorCount) return [];
    return [
      { id: "ok", value: data.count - data.errorCount, label: "OK", color: OK_COLOR },
      { id: "error", value: data.errorCount, label: "Error", color: ERROR_COLOR },
    ];
  }, [data]);

  const protoData = useMemo(() => {
    if (!data?.protocols || !showHostExtras) return [];
    return data.protocols.map((p, i) => ({
      id: p.protocol, value: p.count, label: p.protocol,
      color: PALETTE[i % PALETTE.length],
    }));
  }, [data, showHostExtras]);

  if (loading) return <Skeleton variant="rounded" height={260} />;
  if (error) {
    return (
      <Typography sx={{ color: "#ff4444", fontSize: 13, py: 3, textAlign: "center" }}>
        Failed to load activity stats — {error}
      </Typography>
    );
  }
  if (!data || data.count === 0) {
    return (
      <Typography sx={{ color: colors.grey[600], fontSize: 13, py: 3, textAlign: "center" }}>
        No communications in this period
      </Typography>
    );
  }

  return (
    <>
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2.5 }}>
        <MiniStat label="Communications" value={data.count} />
        <MiniStat label="Avg. interval" value={fmtDuration(data.avgIntervalSeconds)} />
        <MiniStat label="Reads" value={data.readCount} color={READ_COLOR} />
        <MiniStat label="Writes" value={data.writeCount} color={WRITE_COLOR} />
        <MiniStat label="Error rate" value={`${(data.errorRate * 100).toFixed(1)}%`} color={data.errorCount > 0 ? ERROR_COLOR : OK_COLOR} />
        <MiniStat label="Avg. latency" value={fmtLatency(data.avgLatencyMs)} />
      </Box>

      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "flex-start" }}>
        <MiniPie title="Read / Write" data={rwData} />
        {errorData.length > 0 && <MiniPie title="OK / Error" data={errorData} />}
        {showHostExtras && <MiniPie title="Protocols" data={protoData} />}
        <HourlyBar hourly={data.hourly} />
      </Box>

      {showHostExtras && <TopTagsBar topTags={data.topTags} />}
    </>
  );
};

const StatsCard = ({ title, mono, children }) => (
  <Box sx={{ width: "100%", p: 3, borderRadius: 2, border: "1px solid rgba(69,144,149,0.15)", backgroundColor: "rgba(20,20,20,0.8)" }}>
    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#459095", textTransform: "uppercase", letterSpacing: 0.8, mb: 2, fontFamily: mono ? "monospace" : undefined }}>
      {title}
    </Typography>
    {children}
  </Box>
);

/**
 * Host + tag activity stats, stacked full-width (own tab, plenty of room for larger charts) —
 * read/write split, error rate, latency, protocol mix and top-tags (host only), hourly activity
 * pattern. Both scopes are fetched independently and always rendered together; the tag card is
 * replaced by a placeholder until a signal is selected in the Chart tab. Recomputed server-side
 * whenever the time range, device or signal selection changes.
 *
 * @param {string}      ip           - selected device ip (host) — required, hides everything if absent
 * @param {string|null} fullAddress  - selected signal's full_address; enables the Tag card when set
 * @param {string|null} signalLabel  - display name for the selected signal (used in the Tag title)
 * @param {Array|null}  timeFilter   - [isoFrom, isoTo], same shape as OtTimeSeriesChart's timeFilter
 */
const OtActivityStats = ({ ip, fullAddress = null, signalLabel = null, timeFilter = null }) => {
  const hostPayload = useMemo(
    () => (ip && timeFilter ? { ip, timeFilter } : null),
    [ip, JSON.stringify(timeFilter)], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const tagPayload = useMemo(
    () => (ip && fullAddress && timeFilter ? { ip, full_address: fullAddress, timeFilter } : null),
    [ip, fullAddress, JSON.stringify(timeFilter)], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const host = useOtActivityStats(hostPayload);
  const tag = useOtActivityStats(tagPayload);

  if (!ip) {
    return (
      <Typography sx={{ color: colors.grey[600], fontSize: 13, py: 4, textAlign: "center" }}>
        Select a device in the Chart tab to see its activity stats
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <StatsCard title={`Host activity — ${ip}`}>
        <ScopeBody data={host.data} loading={host.loading} error={host.error} showHostExtras />
      </StatsCard>

      {fullAddress ? (
        <StatsCard title={`Tag activity — ${signalLabel ?? fullAddress}`} mono>
          <ScopeBody data={tag.data} loading={tag.loading} error={tag.error} showHostExtras={false} />
        </StatsCard>
      ) : (
        <Box sx={{ width: "100%", py: 4, display: "flex", alignItems: "center", justifyContent: "center", color: colors.grey[600], fontSize: 13, border: `1px dashed ${colors.grey[800]}`, borderRadius: 2 }}>
          Select a signal in the Chart tab to see its stats
        </Box>
      )}
    </Box>
  );
};

export default OtActivityStats;
